import { ref, watch } from 'vue';
import type { Ref } from 'vue';
import { useRoute, useRouter } from 'vue-router/composables';
import { globalStore } from 'src/stores/global-store';
import { checkRole } from 'src/views/admin/utils/role-check';
import { ClientRole } from '@cognitoforms/types/server-types/core/services/client-role';
import { useEnhancedSecurityGate } from 'src/composables/use-enhanced-security-gate';
import { getIncompleteCognitoPayAccounts, getOnboardedCognitoPayAccounts } from 'src/views/admin/cognito-pay/utils/cognito-pay-utils';
import type { CognitoPayRequirementReason } from 'src/components/app/CognitoPayRequirementDialog.vue';

export function useCognitoPayOnboardingNav(options: {
	orgCode: Ref<string>;
	hasIncompleteCognitoPayAccount: Ref<boolean>;
}) {
	const route = useRoute();
	const router = useRouter();
	const showCognitoPayOnboarding = ref(false);
	const showCognitoPayRequirement = ref(false);
	const cognitoPayRequirementReason = ref<CognitoPayRequirementReason>('owner');
	const cognitoPayOnboardingAccountId = ref('');

	const { isSessionGateBlocking } = useEnhancedSecurityGate({ requireEnhancedSecurity: false });

	const requestOnboarding = (accountId: string) => {
		cognitoPayOnboardingAccountId.value = accountId;

		if (!checkRole(ClientRole.Owner)) {
			cognitoPayRequirementReason.value = 'owner';
			showCognitoPayRequirement.value = true;
			return;
		}

		if (isSessionGateBlocking.value) {
			cognitoPayRequirementReason.value = 'mfa';
			showCognitoPayRequirement.value = true;
			return;
		}

		showCognitoPayOnboarding.value = true;
	};

	const handlePaymentClicked = () => {
		if (!options.hasIncompleteCognitoPayAccount.value)
			return;

		const incompleteAccount = getIncompleteCognitoPayAccounts(globalStore.organization?.PaymentAccounts)[0];
		if (incompleteAccount)
			requestOnboarding(incompleteAccount.Id);
	};

	const handleRequirementVerified = () => {
		showCognitoPayRequirement.value = false;
		showCognitoPayOnboarding.value = true;
	};

	watch(() => route.query.resumeCognitoPayOnboarding as string, (resumeAccountId) => {
		if (resumeAccountId) {
			const {
				resumeCognitoPayOnboarding: _resumeCognitoPayOnboarding,
				startCognitoPayOnboarding: _startCognitoPayOnboarding,
				...restQuery
			} = route.query;
			const paymentAccounts = globalStore.organization?.PaymentAccounts;
			const onboardedAccount = getOnboardedCognitoPayAccounts(paymentAccounts).find(a => a.Id === resumeAccountId);

			if (onboardedAccount) {
				router.replace({ name: 'payment-account', params: { orgcode: options.orgCode.value }, query: { accountId: onboardedAccount.Id } });
			}
			else {
				const incompleteAccount = getIncompleteCognitoPayAccounts(paymentAccounts).find(a => a.Id === resumeAccountId);
				if (incompleteAccount)
					requestOnboarding(incompleteAccount.Id);

				router.replace({ query: restQuery });
			}
		}
	}, { immediate: true });

	// Entry point for orgs with no Cognito Pay account yet, e.g. the integrations upsell. Onboarding opens with no
	// account id, so the embed starts on its business type pre-screen and creates the account.
	watch(() => route.query.startCognitoPayOnboarding as string, (startOnboarding) => {
		if (!startOnboarding || route.query.resumeCognitoPayOnboarding)
			return;

		const { startCognitoPayOnboarding: _startCognitoPayOnboarding, ...restQuery } = route.query;

		requestOnboarding('');
		router.replace({ query: restQuery });
	}, { immediate: true });

	return {
		showCognitoPayOnboarding,
		cognitoPayOnboardingAccountId,
		showCognitoPayRequirement,
		cognitoPayRequirementReason,
		handlePaymentClicked,
		handleRequirementVerified
	};
}
